// kalanjay/pages/KalanjayWaitlist.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Stack,
  InputAdornment,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as yup from "yup";
import { formatWithTzLabel } from "../../../utils/dateUtils";

import AppTable from "../../../common/app-table";
import DashboardBadge from "../../../common/dashboard-badges";
import ButtonOne from "../../../common/normal-button";
import AppDatePicker from "../../../common/app-date-picker";
import AppChip from "../../../common/app-chip";
import { CallingCodeSelect } from "../../../common/country-code-select";
import { getCallingCodePhoneError } from "../../../common/phone-validation";
import { useTenantCountryDefault } from "../../../hooks/useTenantCountry";
import typography from "../../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
  dashboardCardGreenTextColor,
  inputColor,
} from "../../../cssUtils/constants";
import {
  CONVERT_KALANJAY_WAITLIST,
  CREATE_KALANJAY_WAITLIST,
  DELETE_KALANJAY_WAITLIST,
  GET_KALANJAY_BATCHES,
  GET_KALANJAY_CENTRES,
  GET_KALANJAY_AGE_GROUPS,
  GET_KALANJAY_WAITLIST,
  UPDATE_KALANJAY_WAITLIST,
} from "../../../config/urls";
import axios from "axios";
import { useAuth } from "../../../hooks/AuthProvider";
import NoPermission from "../../../common/NoPermission";
import { DateTimePicker } from "@mui/x-date-pickers";
import { ArrowBack } from "@mui/icons-material";

// ── helpers ──────────────────────────────────────────────────────────────────
const calcAge = (dob) => {
  if (!dob) return "—";
  const diff = Date.now() - new Date(dob).getTime();
  const a = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  return a > 0 ? a + " yrs" : "—";
};
const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d || "—";
  }
};

const SCHOOL_GRADES = [
  "Pre-Primary",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
  "Class 9",
  "Class 10",
  "Class 11",
  "Class 12",
  "College",
];

// ── shared field sx ───────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 40,
    ...typography.bodyLarge,
    color: inputColor,
    "& fieldset": { borderColor: borderColors.cardBorderPrimaryGrayColor },
    "&:hover fieldset": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused fieldset": { borderColor: ColorsConstants.primaryPurple100 },
  },
};
const textAreaSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    ...typography.bodyLarge,
    color: inputColor,
    "& fieldset": { borderColor: borderColors.cardBorderPrimaryGrayColor },
    "&:hover fieldset": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused fieldset": { borderColor: ColorsConstants.primaryPurple100 },
  },
};

const FL = ({ label, children }) => (
  <Box>
    <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 0.5 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

// ── Phone input ───────────────────────────────────────────────────────────────
const PhoneInput = ({ countryCode, phone, onCC, onPhone }) => (
  <Box
    sx={{
      display: "flex",
      border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
      borderRadius: "8px",
      overflow: "hidden",
      height: 40,
      alignItems: "center",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        backgroundColor: "#F8F7F4",
        borderRight: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        flexShrink: 0,
      }}
    >
      <CallingCodeSelect
        value={countryCode}
        onChange={(code) => onCC({ target: { value: code } })}
        label=""
        size="small"
        sx={{ width: 190 }}
        TextFieldProps={{
          variant: "standard",
          sx: {
            "& .MuiInputBase-input": { ...typography.bodyLarge, fontWeight: 700, color: inputColor },
            "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
          },
        }}
      />
    </Box>
    <TextField
      variant="standard"
      value={phone}
      onChange={onPhone}
      placeholder="98765 43210"
      inputProps={{ maxLength: 12 }}
      sx={{
        flex: 1,
        pl: 1.5,
        "& input": { ...typography.bodyLarge, color: inputColor },
        "& .MuiInput-underline:before": { display: "none" },
        "& .MuiInput-underline:after": { display: "none" },
      }}
    />
  </Box>
);

// ── Status chip ───────────────────────────────────────────────────────────────
const WStatusChip = ({ status }) => {
  const map = {
    Assigned: { bg: "#E8F5E9", color: dashboardCardGreenTextColor },
    Contacted: {
      bg: ColorsConstants.primaryBlue60,
      color: ColorsConstants.primaryPurple100,
    },
    Waiting: { bg: "#FEF4E0", color: "#C07000" },
  };
  const s = map[status] || map.Waiting;
  return (
    <AppChip
      label={status}
      sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }}
    />
  );
};

const resolveChildAge = (groupId, groups) => {
  const g = groups?.find((x) => x.id === groupId);
  if (!g) return null;
  if (g.min_age === g.max_age) return g.min_age;
  if (g.min_age != null) return g.min_age;
  return null;
};

// ── Waitlist Add/Edit Dialog ──────────────────────────────────────────────────
const WaitlistDialog = ({
  open,
  onClose,
  onSave,
  editData,
  allBatches,
  allCentres,
  defaultCentreId,
  mapApiToForm,
  ageGroup,
  ageGroups
}) => {
  const blank = {
    parentName: "",
    childName: "",
    countryCode: "",
    phone: "",
    childDob: "",
    childAge: resolveChildAge(ageGroup, ageGroups),
    ageGroupId: ageGroup,
    schoolName: "",
    childGrade: "",
    waitlistDate: "",
    notes: "",
    status: "Waiting",
    batchId: "",
    centreId: defaultCentreId || "",
  };
  const [f, setF] = useState(blank);
  const [errors, setErrors] = useState({});
  // New records start on the tenant's country (Tenant Settings)
  useTenantCountryDefault(f.countryCode, (v) => setF((p) => ({ ...p, countryCode: v })), { enabled: !editData });
  React.useEffect(() => {
    setF(editData ? { ...editData } : blank);
  }, [open]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  // Typed numbers are checked live against the selected country; the save error shows while empty
  const phoneError = f.phone ? getCallingCodePhoneError(f.phone, f.countryCode) : errors.phone;

  const waitlistSchema = yup.object().shape({
    parentName: yup.string().optional(),
    childName: yup.string().required("Child name is required"),
    phone: yup
      .string()
      .required("Phone is required")
      .test("valid-phone", function (value) {
        const { countryCode } = this.parent;
        const error = getCallingCodePhoneError(value, countryCode);
        return error ? this.createError({ message: error }) : true;
      }),
    childDob: yup.string(), // .required("DOB is required"),
    childAge: yup
      .number()
      .nullable(),
    // schoolName: yup.string().required("School name is required"),
    // childGrade: yup.string().required("Grade is required"),
    waitlistDate: yup.string().required("Waitlist date & time is required"),
    status: yup.string().required(),
    batchId: yup
      .string()
      .transform((value) => (value === "" ? null : value))
      .nullable()
      .when("status", {
        is: "Assigned",
        then: (schema) =>
          schema.required("Batch is required when status is Assigned"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const validateForm = async (f) => {
    try {
      await waitlistSchema.validate(f, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const eObj = {};
      err.inner.forEach((e) => {
        eObj[e.path] = e.message;
      });
      setErrors(eObj);
      return false;
    }
  };

  React.useEffect(() => {
    if (editData) {
      setF(mapApiToForm(editData));
    } else {
      setF(blank);
    }
  }, [editData, open]);
  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setErrors({});
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", m: { xs: 1, sm: 2 } } }}
    >
      <DialogTitle>
        <Typography sx={typography.cardTitle}>
          {editData ? "Edit Waitlist Entry" : "Add to Waitlist"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Parent + Child */}
          <Grid item xs={12} sm={6}>
            <FL label="Parent Name (Optional)">
              <TextField
                fullWidth
                size="small"
                value={f.parentName}
                onChange={set("parentName")}
                placeholder="Parent's full name"
                sx={fieldSx}
              />
            </FL>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FL label="Child Name">
              <TextField
                fullWidth
                size="small"
                value={f.childName}
                onChange={set("childName")}
                placeholder="Child's full name"
                sx={fieldSx}
                error={!!errors.childName}
                helperText={errors.childName}
              />
            </FL>
          </Grid>

          {/* Phone */}
          <Grid item xs={12}>
            <FL label="Phone Number">
              <PhoneInput
                countryCode={f.countryCode}
                phone={f.phone}
                onCC={set("countryCode")}
                onPhone={set("phone")}
              />
              {phoneError && (
                <Typography color="error" variant="caption">
                  {phoneError}
                </Typography>
              )}
            </FL>
          </Grid>

          {/* DOB */}
          <Grid item xs={12} sm={6}>
            <FL label="Child Date of Birth (Optional)">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <AppDatePicker
                  value={f.childDob ? dayjs(f.childDob) : null}
                  onChange={(v) =>
                    setF((p) => ({
                      ...p,
                      childDob: v ? v.format("YYYY-MM-DD") : "",
                    }))
                  }
                />
              </LocalizationProvider>

              {errors.childDob && (
                <Typography color="error" variant="caption">
                  {errors.childDob}
                </Typography>
              )}
            </FL>
          </Grid>

          {/* Age */}
          <Grid item xs={12} sm={6}>
            <FL label="Current Age">
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select
                  value={f.ageGroupId || ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setF((prev) => ({
                      ...prev,
                      childAge: resolveChildAge(selectedId, ageGroups),
                      ageGroupId: selectedId,
                    }));
                  }}
                  displayEmpty
                >
                  {ageGroups?.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FL>
          </Grid>

          {/* School */}
          <Grid item xs={12} sm={6}>
            <FL label="School Name (Optional)">
              <TextField
                fullWidth
                size="small"
                value={f.schoolName}
                onChange={set("schoolName")}
                placeholder="School name"
                sx={fieldSx}
                error={!!errors.schoolName}
                helperText={errors.schoolName}
              />
            </FL>
          </Grid>

          {/* Grade */}
          <Grid item xs={12} sm={6}>
            <FL label="Current Grade (Optional)">
              <TextField
                fullWidth
                size="small"
                value={f.childGrade || ""}
                onChange={set("childGrade")}
                placeholder="Enter grade (e.g. Class 5)"
                sx={fieldSx}
                error={!!errors.childGrade}
                helperText={errors.childGrade}
              />
            </FL>
          </Grid>

          {/* Waitlist Date */}
          <Grid item xs={12} sm={6}>
            <FL label="Waitlist Date & Time">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  value={f.waitlistDate ? dayjs(f.waitlistDate) : null}
                  onChange={(v) =>
                    setF((p) => ({
                      ...p,
                      waitlistDate: v ? v.format("YYYY-MM-DDTHH:mm:ss") : "",
                    }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: fieldSx,
                    },
                  }}
                />
              </LocalizationProvider>

              {errors.waitlistDate && (
                <Typography color="error" variant="caption">
                  {errors.waitlistDate}
                </Typography>
              )}
            </FL>
          </Grid>

          {/* Preferred Centre */}
          <Grid item xs={12} sm={6}>
            <FL label="Preferred Centre">
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select
                  value={f.centreId || ""}
                  onChange={set("centreId")}
                  displayEmpty
                >
                  <MenuItem value="">
                    <Typography
                      sx={{ ...typography.bodyLarge, color: grayFontColor }}
                    >
                      — Not specified —
                    </Typography>
                  </MenuItem>
                  {allCentres.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FL>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <FL label="Status">
              <FormControl
                fullWidth
                size="small"
                sx={fieldSx}
                error={!!errors.status}
              >
                <Select value={f.status} onChange={set("status")}>
                  <MenuItem value="Waiting">Waiting</MenuItem>
                  {/* <MenuItem value="Contacted">Contacted</MenuItem> */}
                  <MenuItem value="Assigned">Assigned</MenuItem>
                </Select>

                {errors.status && (
                  <Typography color="error" variant="caption">
                    {errors.status}
                  </Typography>
                )}
              </FormControl>
            </FL>
          </Grid>

          {/* Assign Batch */}
          <Grid item xs={12} sm={6}>
            <FL label="Assign Batch">
              <FormControl
                fullWidth
                size="small"
                sx={fieldSx}
                error={!!errors.batchId}
              >
                <Select
                  value={f.batchId || ""}
                  onChange={set("batchId")}
                  displayEmpty
                  disabled={f.status !== "Assigned"} // 🔥 KEY FIX
                >
                  <MenuItem value="">
                    <Typography
                      sx={{ ...typography.bodyLarge, color: grayFontColor }}
                    >
                      — Not assigned —
                    </Typography>
                  </MenuItem>

                  {allBatches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name} — {b.centre_name}
                    </MenuItem>
                  ))}
                </Select>

                {errors.batchId && (
                  <Typography color="error" variant="caption">
                    {errors.batchId}
                  </Typography>
                )}
              </FormControl>
            </FL>
          </Grid>

          {/* Notes */}
          <Grid item xs={12} sm={6}>
            <FL label="Notes">
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={f.notes}
                onChange={set("notes")}
                placeholder="Preferred batch, any notes…"
                sx={textAreaSx}
                error={!!errors.notes}
                helperText={errors.notes}
              />
            </FL>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, gap: 1, flexWrap: "wrap" }}>
        <ButtonOne variant="outlined" onClick={onClose}>
          Cancel
        </ButtonOne>
        <ButtonOne
          onClick={async () => {
            console.log(f)
            const valid = await validateForm(f);
            console.log("Form valid?", valid, f);
            if (!valid) return;
            onSave(f);
          }}
        >
          {editData ? "Save Changes" : "Add to Waitlist"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// KalanjayWaitlist
// Props:
//   waitlist / setWaitlist — state + setter
//   allBatches             — array of all batch objects (for assign dropdown)
//   showToast              — (msg) => void
// ══════════════════════════════════════════════════════════════════════════════
const KalanjayWaitlist = ({ ageGroup, ageGroupName, defaultCentreId, showToast, onBack }) => {
  const [ageGroups, setAgeGroups] = useState([]);
  const permissions = localStorage.getItem("permissions") || [];
  const hasViewPermission = permissions.includes("waitlist.view");
  const hasCreatePermission = permissions.includes("waitlist.create");
  const hasEditPermission = permissions.includes("waitlist.edit");
  const [allBatches, setAllBatches] = useState([]);
  const [allCentres, setAllCentres] = useState([]);
  const [search, setSearch] = useState("");

  const fetchAgeGroups = async (centreId = defaultCentreId || "") => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      let url = GET_KALANJAY_AGE_GROUPS;
      if (centreId) url += `?centre=${centreId}`;

      const res = await axios.get(url, { headers });
      const data = res?.data?.results || res.data;
      setAgeGroups(data);
    } catch (err) {
      console.error("Error fetching age groups:", err);
      return [];
    }
  };

  const fetchBatches = async (
    limit = 100,
    offset = 0,
    search = "",
    centreId = "",
  ) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const params = new URLSearchParams({ limit, offset });
      if (search) params.append("search", search);
      if (centreId) params.append("centre", centreId);

      const res = await axios.get(
        `${GET_KALANJAY_BATCHES}?${params.toString()}`,
        { headers },
      );
      const batchesFromApi = res?.data?.results || res.data;
      setAllBatches(batchesFromApi);
    } catch (err) {
      console.error(err);
    }
  };
  const [waitlist, setWaitlist] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [waitingCount, setWaitingCount] = useState(0);
  const [assignedCount, setAssignedCount] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const { getValidToken } = useAuth();
  let _wc = waitlist.length + 100;

  useEffect(() => {
    if (ageGroup) {
      fetchWaitlist(20, 0, ageGroup);
    }
  }, [ageGroup]);

  useEffect(() => {
    fetchBatches();
    fetchCentres();
    fetchAgeGroups();
  }, []);

  const fetchCentres = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };
      const res = await axios.get(GET_KALANJAY_CENTRES, { headers });
      setAllCentres(res?.data?.results || res.data);
    } catch (err) {
      console.error(err);
    }
  };
  const fetchWaitlist = async (
    limit = 20,
    offset = 0,
    selectedAgeGroup = null,
    searchQuery = "",
    centreId = defaultCentreId || "",
  ) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      let url = `${GET_KALANJAY_WAITLIST}?limit=${limit}&offset=${offset}&search=${encodeURIComponent(searchQuery)}`;

      if (selectedAgeGroup) {
        url += `&age_group_id=${selectedAgeGroup}`;
      }
      if (centreId) {
        url += `&centre=${centreId}`;
      }

      const res = await axios.get(url, { headers });
      const data = res?.data?.results || res.data;
      const count = res?.data?.count ?? data.length;

      setWaitlist(data);
      setTotalCount(count);
      setWaitingCount(res?.data?.waiting_count ?? 0);
      setAssignedCount(res?.data?.assigned_count ?? 0);
    } catch (err) {
      console.error("Error fetching waitlist:", err);
      return [];
    }
  };

  const onPageChange = (_, newPage) => {
    setPage(newPage);
    fetchWaitlist(rowsPerPage, newPage * rowsPerPage, ageGroup, search);
  };

  const onRowsPerPageChange = (e) => {
    const val = Number(e?.target?.value);
    if (!val || isNaN(val)) return;
    setRowsPerPage(val);
    setPage(0);
    fetchWaitlist(val, 0, ageGroup, search);
  };

  const mapApiToForm = (data) => {
    const childAge = data.child_age;
   
    const matchedGroup = ageGroups.find(
       (g) =>
         childAge !== null &&
         g.min_age !== null &&
         childAge >= g.min_age &&
         (g.max_age === null || childAge <= g.max_age)
    );
    
    return {
      id: data.id,
      parentName: data.parent_name || "",
      childName: data.child_name || "",
      countryCode: data.country_code?.replace("+", "") || "91",
      phone: data.phone || "",
      childDob: data.child_dob || "",
      childAge: childAge || "",
      ageGroupId: matchedGroup?.id || "", 
      schoolName: data.school_name || "",
      childGrade: data.child_grade || "",
      waitlistDate: data.waitlist_datetime || "",
      notes: data.notes || "",
      status: data.status || "Waiting",
      batchId: data.assigned_batch || "",
      centreId: data.preferred_center || "",
    }
  };

  const mapFormToApi = (f) => ({
    parent_name: f.parentName,
    country_code: `+${f.countryCode}`,
    phone: f.phone,
    child_name: f.childName,
    child_dob: f.childDob || null,
    child_age: f.childAge || null,
    school_name: f.schoolName,
    child_grade: f.childGrade,
    waitlist_datetime: f.waitlistDate ? dayjs(f.waitlistDate).format("YYYY-MM-DDTHH:mm:ss") : null,
    status: f.status,
    assigned_batch: f.batchId || null,
    preferred_center: f.centreId || null,
    notes: f.notes,
  });

  const addToWailist = async (f) => {
    if (!f.childName) return;

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const payload = {
        parent_name: f.parentName,
        country_code: `+${f.countryCode}`,
        phone: f.phone,
        child_name: f.childName,
        child_age: f.childAge,
        child_dob: f.childDob || null,
        school_name: f.schoolName,
        child_grade: f.childGrade,
        status: f.status,
        assigned_batch: f.batchId || null,
        preferred_center: f.centreId || null,
        notes: f.notes,
        waitlist_datetime: f.waitlistDate || null,
      };

      await axios.post(CREATE_KALANJAY_WAITLIST, payload, { headers });

      setDialogOpen(false);
      setEditData(null);
      await fetchWaitlist(rowsPerPage, 0, ageGroup);
    } catch (err) {
      console.error(err);
    }
  };

  const updateWaitlist = async (f) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const payload = mapFormToApi(f);
      console.log(payload)

      const res = await axios.patch(UPDATE_KALANJAY_WAITLIST(f.id), payload, {
        headers,
      });

      // Sorting by waitlist_datetime
      // setWaitlist((prev) =>
      //   prev
      //     .map((item) => (item.id === f.id ? res.data : item))
      //     .sort(
      //       (a, b) =>
      //         new Date(a.waitlist_datetime) - new Date(b.waitlist_datetime),
      //     ),
      // );

      setDialogOpen(false);
      setEditData(null);

      await fetchWaitlist(20, 0, ageGroup);
      showToast?.("Waitlist updated");
    } catch (err) {
      console.error(err);
      showToast?.("Failed to update waitlist");
    }
  };

  const deleteFromWaitlist = async (id) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      await axios.delete(DELETE_KALANJAY_WAITLIST(id), { headers });

      // ✅ update UI (remove deleted item)
      setWaitlist((prev) => prev.filter((item) => item.id !== id));

      showToast?.("Waitlist entry deleted");
    } catch (err) {
      console.error(err);
      showToast?.("Failed to delete waitlist entry");
    }
  };

  // ── table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      id: "parent_name",
      label: "Parent Name",
      render: (r) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
          {r.parent_name}
        </Typography>
      ),
    },
    {
      id: "child_name",
      label: "Child Name",
      render: (r) => (
        <Typography sx={typography.bodyLarge}>{r.child_name}</Typography>
      ),
    },
    {
      id: "phone",
      label: "Phone",
      render: (r) => (
        <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
          {r.country_code} {r.phone}
        </Typography>
      ),
    },
    {
      id: "child_age",
      label: "Current Age",
      render: (r) => (
        <Typography
          sx={{
            ...typography.bodyLarge,
            fontWeight: 600,
            color: ColorsConstants.primaryGray80,
          }}
        >
          {r.child_age ? `${r.child_age} yrs` : "—"}
        </Typography>
      ),
    },
    {
      id: "child_dob",
      label: "Date of Birth",
      render: (r) => (
        <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
          {r.child_dob ? fmtDate(r.child_dob) : "—"}
        </Typography>
      ),
    },
    {
      id: "child_grade",
      label: "Current Grade",
      render: (r) =>
        r.child_grade ? (
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
            {r.child_grade}
          </Typography>
        ) : (
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            —
          </Typography>
        ),
    },
    {
      id: "school_name",
      label: "School",
      render: (r) => (
        <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
          {r.school_name || "—"}
        </Typography>
      ),
    },
    {
      id: "waitlist_date",
      label: "Waitlist Date",
      render: (r) => (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          {formatWithTzLabel(r.waitlist_datetime_iso, "DD MMM YYYY, hh:mm A")}
        </Typography>
      ),
    },
    {
      id: "preferred_centre_name",
      label: "Centre",
      render: (r) => (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          {r.preferred_centre_name || "—"}
        </Typography>
      ),
    },
    {
      id: "assigned_batch",
      label: "Assigned Batch",
      render: (r) =>
        r.assigned_batch_name ? (
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
            {r.assigned_batch_name}
          </Typography>
        ) : (
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            —
          </Typography>
        ),
    },
    {
      id: "notes",
      label: "Notes",
      render: (r) => (
        <Typography
          sx={{
            ...typography.bodySmall,
            color: grayFontColor,
            maxWidth: { xs: 100, sm: 160, md: 160 },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {r.notes || "—"}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (r) => <WStatusChip status={r.status} />,
    },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (r) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <IconButton
            size="small"
            title="Edit"
            onClick={() => {
              if (hasEditPermission) {
                setEditData(r);
                setDialogOpen(true);
              }
            }}
            sx={{
              color: hasEditPermission
                ? ColorsConstants.primaryGray60
                : ColorsConstants.primaryGray20,
              "&:hover": {
                backgroundColor: hasEditPermission
                  ? ColorsConstants.primaryBlue60
                  : ColorsConstants.primaryGray20,
              },
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            title="Remove"
            onClick={() => deleteFromWaitlist(r.id)}
            sx={{
              color: ColorsConstants.error,
              "&:hover": { backgroundColor: "#FDECEA" },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleSave = async (f) => {
    if (f.id) {
      await updateWaitlist(f); // EDIT
    } else {
      await addToWailist(f); // CREATE
    }
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    fetchWaitlist(rowsPerPage, 0, ageGroup, val);
  };

  if (!hasViewPermission) {
    return (
      <Stack sx={{ mt: 16 }}>
        <NoPermission />
      </Stack>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* <Typography sx={{ ...typography.h1, mb: 3 }}>Waitlist</Typography> */}

      {/* Back + Title */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 1.5, mb: 3 }}>
        <IconButton
          onClick={onBack}
          sx={{
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: "8px",
            p: 0.75,
            color: grayFontColor,
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography sx={{ ...typography.h1, fontSize: { xs: 22, md: typography.h1.fontSize } }}>
          Waitlist {ageGroupName}
        </Typography>
        <Box sx={{ ml: { xs: 0, md: "auto" }, width: { xs: "100%", sm: "auto" } }}>
          <ButtonOne
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => {
              setEditData(null);
              setDialogOpen(true);
            }}
            disabled={!hasCreatePermission}
          >
            Add to Waitlist
          </ButtonOne>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            value: waitingCount + assignedCount,
            // label: "All entries",
            textColor: ColorsConstants.primaryBackgroundColor,
            title: "Total Waitlist",
          },
          {
            value: waitingCount,
            // label: "Waiting",
            textColor: "#C07000",
            title: "Waiting",
          },
          // {
          //   value: waitlist.filter((w) => w.status === "Contacted").length,
          //   label: "Contacted",
          //   textColor: ColorsConstants.primaryPurple100,
          //   title: "Contacted",
          // },
          {
            value: assignedCount,
            // label: "Batch assigned",
            textColor: dashboardCardGreenTextColor,
            title: "Assigned",
          },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <DashboardBadge
              title={item.title}
              value={item.value}
              // label={item.label}
              textColor={item.textColor}
            />
          </Grid>
        ))}
      </Grid>

      {/* Table header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-start", md: "space-between" },
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1.5,
          
        }}
      >
        <Typography sx={typography.cardTitle}>
          Waitlist{" "}
          <Typography
            component="span"
            sx={{ ...typography.bodyLarge, color: grayFontColor }}
          >
            ({waitingCount})
          </Typography>
        </Typography>

        {/* Add the Search input here */}
        <TextField
          size="small"
          placeholder="Search by parent name, child name, phone…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: grayFontColor, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ ...fieldSx, width: { xs: "100%", sm: 360, md: 360 } }}
        />
      </Box>

      <AppTable
        columns={columns}
        rows={waitlist}
        rowKey="id"
        emptyMessage="No waitlist entries yet."
        maxHeight={600}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />

      <WaitlistDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditData(null);
        }}
        onSave={handleSave}
        editData={editData}
        allBatches={allBatches}
        allCentres={allCentres}
        defaultCentreId={defaultCentreId}
        mapApiToForm={mapApiToForm}
        ageGroup={ageGroup}
        ageGroups={ageGroups}
      />
    </Box>
  );
};

export default KalanjayWaitlist;

